import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const filterChips = ["ALL", "EDUCATION", "ADVOCACY", "COMMUNITY", "RESOURCES"];
const coverPalettes = [{
  bg: "bg-green-900",
  text: "text-green-50",
  rule: "bg-green-600",
  accent: "text-green-600"
}, {
  bg: "bg-green-600",
  text: "text-green-50",
  rule: "bg-green-50",
  accent: "text-green-50"
}, {
  bg: "bg-green-950",
  text: "text-green-50",
  rule: "bg-green-600",
  accent: "text-green-600"
}, {
  bg: "bg-green-900",
  text: "text-green-50",
  rule: "bg-green-600",
  accent: "text-green-600"
}, {
  bg: "bg-green-600",
  text: "text-green-50",
  rule: "bg-green-50",
  accent: "text-green-50"
}, {
  bg: "bg-green-950",
  text: "text-green-50",
  rule: "bg-green-600",
  accent: "text-green-600"
}];
function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function estimateReadingTime(content?: string, excerpt?: string): string {
  const text = stripHtml(content) || stripHtml(excerpt) || "";
  const words = text.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(3, Math.ceil(words / 200));
  return `${minutes} MIN READ`;
}
function RecentChaptersGrid() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="1156" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[3/4] bg-green-100 border border-green-200 animate-pulse rounded-lg" />)}
      </div>;
  }
  if (error) {
    return <div className="border border-green-200 bg-green-50 p-10 text-center rounded-lg">
        <p className="font-default italic text-green-900/80 mb-4">
          The awareness library is currently unavailable.
        </p>
        <Button onClick={refetch} variant="outline" className="font-default uppercase tracking-[0.16em] text-xs rounded-lg border-green-900 text-green-900 hover:bg-green-900 hover:text-green-50">
          Try Again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="border border-green-200 bg-green-50 p-16 text-center rounded-lg">
        <p className="font-default uppercase tracking-[0.18em] text-xs text-green-700 mb-3">
          § F.000 / FF — NO UPDATES
        </p>
        <p className="font-default italic text-lg text-green-900/80">
          No awareness articles are currently published. Check back soon.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
      {posts.map((post, i) => {
      const palette = coverPalettes[i % coverPalettes.length];
      const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "UPDATE";
      const indexLabel = `F.${String(i + 1).padStart(3, "0")}${String.fromCharCode(97 + i % 3)}`;
      const editionNo = `No. ${String(i + 1).padStart(2, "0")}`;
      const cleanTitle = stripHtml(post.title);
      return <article data-index={i} key={post.id} className="group bg-green-50 border border-green-200 rounded-lg overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 shadow-md">
            <Link to={post.link || "#"} className="block">
              <div className={`relative aspect-[3/4] ${palette.bg} ${palette.text} overflow-hidden transition-transform duration-300 ease-out group-hover:-rotate-1`}>
                <div className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, hsl(120 38% 92%) 0%, transparent 2%), radial-gradient(circle at 70% 60%, hsl(120 38% 92%) 0%, transparent 1.5%), radial-gradient(circle at 40% 80%, hsl(120 38% 92%) 0%, transparent 2%)"
            }} />

                <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
                  <span className={`font-default text-[0.7rem] uppercase tracking-[0.18em] ${palette.accent}`}>
                    § {indexLabel}
                  </span>
                  <span className={`font-default text-[0.65rem] uppercase tracking-[0.16em] ${palette.text} opacity-70`}>
                    {category}
                  </span>
                </div>

                <div className="absolute inset-x-6 top-1/2 -translate-y-1/2">
                  <h3 className="font-default font-semibold leading-[1.02] tracking-[-0.02em]" style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2.125rem)"
              }}>
                    {cleanTitle}
                  </h3>
                  <div className="mt-6 flex items-center">
                    <span className={`h-px w-16 ${palette.rule} transition-all duration-300 ease-out group-hover:w-32`} />
                    <span className={`ml-2 h-1.5 w-1.5 rounded-lg ${palette.rule}`} />
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <span className={`font-default text-[0.65rem] uppercase tracking-[0.18em] ${palette.text} opacity-80`}>
                    Forever Fresh
                  </span>
                  <span className={`font-default text-[0.65rem] uppercase tracking-[0.16em] ${palette.accent}`}>
                    {editionNo}
                  </span>
                </div>
              </div>

              <div className="px-5 py-4 bg-green-50 border-t border-green-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 font-default text-[0.7rem] uppercase tracking-[0.14em] text-green-900/70">
                  <span>{formatDate(post.date)}</span>
                  <span className="h-1 w-1 rounded-lg bg-green-600" />
                  <span>{category}</span>
                </div>
                <span className="font-default text-[0.7rem] uppercase tracking-[0.14em] text-green-900/70">
                  {estimateReadingTime(post.content, post.excerpt)}
                </span>
              </div>
            </Link>
          </article>;
    })}
    </div>;
}
export default function () {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="humanityawarenesslibrary" className="relative bg-green-50 text-green-950 py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
      backgroundImage: "radial-gradient(circle at 12% 18%, hsl(120 38% 14%) 0%, transparent 1.5%), radial-gradient(circle at 78% 32%, hsl(120 38% 14%) 0%, transparent 1.2%), radial-gradient(circle at 35% 70%, hsl(120 38% 14%) 0%, transparent 1.8%), radial-gradient(circle at 88% 85%, hsl(120 38% 14%) 0%, transparent 1.4%), radial-gradient(circle at 50% 50%, hsl(120 38% 14%) 0%, transparent 1%)"
    }} />

      <div className="relative max-w-[1320px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <p className="font-default text-[0.75rem] uppercase tracking-[0.18em] text-green-700 mb-6">
              § F.001 / FF — AWARENESS LIBRARY
            </p>
            <h2 className="font-default font-bold text-green-950 leading-[1.02] tracking-[-0.02em]" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 4.75rem)"
          }}>
              Keeping issues <em className="italic">forever fresh</em> in mind.
            </h2>
            <div className="mt-8 flex items-center">
              <span className="h-px w-16 bg-green-950" />
              <span className="ml-2 h-1.5 w-1.5 rounded-lg bg-green-600" />
            </div>
          </div>

          <div className="lg:col-span-5 lg:pt-4">
            <p className="font-default italic text-green-900/80 leading-[1.55]" style={{
            fontSize: "clamp(0.95rem, 1.1vw, 1.0625rem)"
          }}>
              Explore our mission-driven articles, educational resources, and community stories that shed light on the causes we support.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {filterChips.map((chip, i) => <Badge data-index={i} key={chip} variant="outline" className="font-default text-[0.65rem] uppercase tracking-[0.16em] rounded-lg border border-green-700 text-green-700 bg-transparent px-2.5 py-1 cursor-pointer hover:bg-green-700 hover:text-green-50 transition-colors">
                  {chip}
                </Badge>)}
            </div>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <RecentChaptersGrid />
        </PostsProvider>
      </div>
    </section>;
}